import { useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Divider,
  Spinner,
  Input,
  Tabs,
  Tab,
} from '@heroui/react'

import { useAuth } from '../hooks/useAuth'
import { useToast } from '../hooks/useToast'
import type { SignInModalProps } from '../types/auth.types'

export const SignInModal = ({ isOpen, onOpenChange }: SignInModalProps) => {
  const {
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
    loading,
    clearError,
    isEmulatorMode,
  } = useAuth()

  const toast = useToast()

  const [signingInWith, setSigningInWith] = useState<
    'google' | 'apple' | 'email' | null
  >(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin')

  const handleGoogleSignIn = async () => {
    try {
      setSigningInWith('google')
      clearError() // Clear any previous errors
      await signInWithGoogle()
      onOpenChange(false)
    } catch (error) {
      console.error('Google sign-in failed:', error)
      // Show error toast and close modal
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign in with Google'
      toast.error(errorMessage)
      onOpenChange(false)
    } finally {
      setSigningInWith(null)
    }
  }

  const handleAppleSignIn = async () => {
    try {
      setSigningInWith('apple')
      clearError() // Clear any previous errors
      await signInWithApple()
      onOpenChange(false)
    } catch (error) {
      console.error('Apple sign-in failed:', error)
      // Show error toast and close modal
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign in with Apple'
      toast.error(errorMessage)
      onOpenChange(false)
    } finally {
      setSigningInWith(null)
    }
  }

  const handleEmailSignIn = async () => {
    if (!email || !password) return

    try {
      setSigningInWith('email')
      await signInWithEmail(email, password)
      onOpenChange(false)
    } catch (error) {
      console.error('Email sign-in failed:', error)
      // Show error toast and close modal
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign in with email'
      toast.error(errorMessage)
      onOpenChange(false)
    } finally {
      setSigningInWith(null)
    }
  }

  const handleEmailSignUp = async () => {
    if (!email || !password || !confirmPassword) return

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    try {
      setSigningInWith('email')
      await signUpWithEmail(email, password)
      onOpenChange(false)
    } catch (error) {
      console.error('Email sign-up failed:', error)
      // Show error toast and close modal
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create account'
      toast.error(errorMessage)
      onOpenChange(false)
    } finally {
      setSigningInWith(null)
    }
  }

  const handleModalClose = () => {
    clearError()
    setSigningInWith(null)
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setActiveTab('signin')
    onOpenChange(false)
  }

  const isLoading = loading || signingInWith !== null

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClose={handleModalClose}
      placement="center"
      backdrop="blur"
      size={isEmulatorMode ? 'md' : 'sm'}
      classNames={{
        base: 'border-none shadow-xl',
        header: 'border-b-1 border-divider',
        body: 'py-6',
        footer: 'border-t-1 border-divider',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold">Sign in to GIF App</h2>
          <p className="text-sm text-default-500 font-normal">
            Choose your preferred sign-in method
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            {/* Social Sign In Options */}
            <div className="flex flex-col gap-3">
              {/* Google Sign In */}
              <Button
                variant="bordered"
                size="lg"
                className="w-full h-12 border-2 hover:border-primary"
                startContent={
                  signingInWith === 'google' ? (
                    <Spinner size="sm" />
                  ) : (
                    <GoogleIcon />
                  )
                }
                onPress={handleGoogleSignIn}
                isDisabled={isLoading}
              >
                {signingInWith === 'google'
                  ? 'Signing in...'
                  : 'Continue with Google'}
              </Button>

              {/* Apple Sign In */}
              <Button
                variant="bordered"
                size="lg"
                className="w-full h-12 border-2 hover:border-primary"
                startContent={
                  signingInWith === 'apple' ? (
                    <Spinner size="sm" />
                  ) : (
                    <AppleIcon />
                  )
                }
                onPress={handleAppleSignIn}
                isDisabled={isLoading}
              >
                {signingInWith === 'apple'
                  ? 'Signing in...'
                  : 'Continue with Apple'}
              </Button>
            </div>

            {/* Email/Password Sign In - Only show in emulator mode */}
            {isEmulatorMode && (
              <>
                {/* Divider */}
                <div className="flex items-center gap-4">
                  <Divider className="flex-1" />
                  <span className="text-xs text-default-400">OR</span>
                  <Divider className="flex-1" />
                </div>

                {/* Email/Password Section */}
                <div className="flex flex-col gap-4">
                  <Tabs
                    selectedKey={activeTab}
                    onSelectionChange={(key) =>
                      setActiveTab(key as 'signin' | 'signup')
                    }
                    variant="underlined"
                    classNames={{
                      tabList: 'w-full',
                      tab: 'flex-1',
                    }}
                  >
                    <Tab key="signin" title="Sign In">
                      <div className="flex flex-col gap-3 mt-4">
                        <Input
                          type="email"
                          label="Email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          isDisabled={isLoading}
                        />
                        <Input
                          type="password"
                          label="Password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isDisabled={isLoading}
                        />
                        <Button
                          color="primary"
                          size="lg"
                          className="w-full h-12"
                          onPress={handleEmailSignIn}
                          isDisabled={isLoading || !email || !password}
                          startContent={
                            signingInWith === 'email' ? (
                              <Spinner size="sm" />
                            ) : null
                          }
                        >
                          {signingInWith === 'email'
                            ? 'Signing in...'
                            : 'Sign In'}
                        </Button>
                      </div>
                    </Tab>
                    <Tab key="signup" title="Sign Up">
                      <div className="flex flex-col gap-3 mt-4">
                        <Input
                          type="email"
                          label="Email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          isDisabled={isLoading}
                        />
                        <Input
                          type="password"
                          label="Password"
                          placeholder="Create a password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isDisabled={isLoading}
                        />
                        <Input
                          type="password"
                          label="Confirm Password"
                          placeholder="Confirm your password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          isDisabled={isLoading}
                          color={
                            confirmPassword && password !== confirmPassword
                              ? 'danger'
                              : 'default'
                          }
                          errorMessage={
                            confirmPassword && password !== confirmPassword
                              ? 'Passwords do not match'
                              : undefined
                          }
                        />
                        <Button
                          color="primary"
                          size="lg"
                          className="w-full h-12"
                          onPress={handleEmailSignUp}
                          isDisabled={
                            isLoading ||
                            !email ||
                            !password ||
                            !confirmPassword ||
                            password !== confirmPassword
                          }
                          startContent={
                            signingInWith === 'email' ? (
                              <Spinner size="sm" />
                            ) : null
                          }
                        >
                          {signingInWith === 'email'
                            ? 'Creating account...'
                            : 'Sign Up'}
                        </Button>
                      </div>
                    </Tab>
                  </Tabs>
                </div>

                {/* Emulator Mode Indicator */}
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 text-center">
                    🧪 Development Mode - Email/Password authentication
                    available
                  </p>
                </div>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            onPress={handleModalClose}
            className="w-full"
            isDisabled={isLoading}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Google Icon Component
const GoogleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

// Apple Icon Component
const AppleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
)
